/**
 * Variant pathogenicity tier resolution and dot-badge configuration.
 *
 * Pure module — no React, no MUI imports — so it can be reused by tests,
 * list views, the pedigree D3 renderer, and chip rendering in VariantRow.jsx.
 *
 * Field-name authority (highest first):
 *   clinicianInterpretation → labInterpretation → clinvarInterpretation
 * Plus *Classification legacy aliases, generic `classification`, and the
 * ClinVar API's bare `description` field.
 *
 * @typedef {'Pathogenic'
 *   | 'Likely Pathogenic'
 *   | 'Variant of Uncertain Significance'
 *   | 'Likely Benign'
 *   | 'Benign'} AcmgTier
 */

const TIER_PATHOGENIC = 'Pathogenic';
const TIER_LIKELY_PATHOGENIC = 'Likely Pathogenic';
const TIER_VUS = 'Variant of Uncertain Significance';
const TIER_LIKELY_BENIGN = 'Likely Benign';
const TIER_BENIGN = 'Benign';

const TIER_RANK = {
  [TIER_PATHOGENIC]: 0,
  [TIER_LIKELY_PATHOGENIC]: 1,
  [TIER_VUS]: 2,
  [TIER_LIKELY_BENIGN]: 3,
  [TIER_BENIGN]: 4
};

// Case-insensitive map from normalised string → canonical AcmgTier.
const NORMALISED_TO_TIER = {
  pathogenic: TIER_PATHOGENIC,
  'likely pathogenic': TIER_LIKELY_PATHOGENIC,
  'variant of uncertain significance': TIER_VUS,
  'uncertain significance': TIER_VUS,
  vus: TIER_VUS,
  'likely benign': TIER_LIKELY_BENIGN,
  benign: TIER_BENIGN
};

const ABSENT_VALUES = new Set([
  '',
  'none',
  'not provided',
  'no description found'
]);

/**
 * Normalise a raw interpretation string to a canonical AcmgTier, or null.
 * @param {unknown} raw
 * @returns {AcmgTier | null}
 */
const normaliseInterpretation = (raw) => {
  if (raw === null || raw === undefined) return null;
  const key = String(raw).trim().toLowerCase();
  if (ABSENT_VALUES.has(key)) return null;
  return NORMALISED_TO_TIER[key] || null;
};

/**
 * Resolve the effective classification of a single variant, honouring the
 * Clinician > Lab > ClinVar authority hierarchy. Returns null when no field
 * yields a recognised tier.
 * @param {object | null | undefined} variant
 * @returns {AcmgTier | null}
 */
export const resolveVariantClassification = (variant) => {
  if (!variant) return null;
  const candidates = [
    variant.clinicianInterpretation,
    variant.clinicianClassification,
    variant.labInterpretation,
    variant.labClassification,
    variant.clinvarInterpretation,
    variant.clinvarClassification,
    variant.classification,
    variant.description
  ];
  for (const candidate of candidates) {
    const tier = normaliseInterpretation(candidate);
    if (tier) return tier;
  }
  return null;
};

/**
 * Worst (highest-severity) tier across all variants attached to a gene.
 * Returns null when no variant yields a recognised tier.
 * @param {Array<object> | null | undefined} variants
 * @returns {AcmgTier | null}
 */
export const resolveGeneBadgeTier = (variants) => {
  if (!Array.isArray(variants) || variants.length === 0) return null;
  let worst = null;
  let worstRank = Infinity;
  for (const variant of variants) {
    const tier = resolveVariantClassification(variant);
    if (!tier) continue;
    const rank = TIER_RANK[tier];
    if (rank < worstRank) {
      worst = tier;
      worstRank = rank;
    }
  }
  return worst;
};

const BADGE_CONFIGS = {
  [TIER_PATHOGENIC]: {
    color: '#D93025',
    label: TIER_PATHOGENIC,
    shortLabel: 'P'
  },
  [TIER_LIKELY_PATHOGENIC]: {
    color: '#F5A623',
    label: TIER_LIKELY_PATHOGENIC,
    shortLabel: 'LP'
  },
  [TIER_VUS]: {
    color: '#9E9E9E',
    label: TIER_VUS,
    shortLabel: 'VUS'
  }
};

/**
 * Visual config for a tier's dot badge. Returns null for tiers that should
 * not render a dot (Likely Benign, Benign, or unknown).
 * @param {AcmgTier | null | undefined} tier
 * @returns {{ color: string, label: string, shortLabel: string } | null}
 */
export const getBadgeConfig = (tier) => {
  if (!tier) return null;
  return BADGE_CONFIGS[tier] || null;
};

/**
 * True when a single variant has tier-level disagreement between its lab
 * and ClinVar interpretations. Both must be present and recognised.
 * @param {object | null | undefined} variant
 * @returns {boolean}
 */
export const hasClassificationConflict = (variant) => {
  if (!variant) return false;
  const lab = normaliseInterpretation(
    variant.labInterpretation ?? variant.labClassification
  );
  const clinvar = normaliseInterpretation(
    variant.clinvarInterpretation ?? variant.clinvarClassification
  );
  if (!lab || !clinvar) return false;
  return lab !== clinvar;
};

/**
 * True when ANY variant in the list has a lab/ClinVar tier-level conflict.
 * @param {Array<object> | null | undefined} variants
 * @returns {boolean}
 */
export const geneHasClassificationConflict = (variants) => {
  if (!Array.isArray(variants)) return false;
  return variants.some(hasClassificationConflict);
};
